import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TenantEmailConfigurationService, TenantEmailConfiguration, SaveEmailConfigurationRequest } from '../../../shared/services/tenant-email-configuration.service';

interface EmailConfigurationForm {
  graphTenantId: string;
  graphClientId: string;
  graphClientSecret: string;
  systemSenderEmail: string;
  isActive: boolean;
  invitationEmailSubject: string;
  invitationEmailBody: string;
  isNotificationEmailEnabled: boolean;
  notificationEmailSubject: string;
  notificationEmailBody: string;
}

@Component({
  selector: 'app-email-editor',
  templateUrl: './email-editor.component.html',
  styleUrls: ['./email-editor.component.scss'],
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailEditorComponent {
  private readonly emailConfigService = inject(TenantEmailConfigurationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly activeTab = signal<'configuration' | 'invitation' | 'notification'>('configuration');
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly isSendingInvitationTest = signal(false);
  readonly isSendingNotificationTest = signal(false);
  readonly hasExistingConfig = signal(false);
  readonly isLocalDevelopment = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly secretKeyVaultName = signal('');
  readonly isSecretConfigured = signal(false);

  formData: EmailConfigurationForm = {
    graphTenantId: '',
    graphClientId: '',
    graphClientSecret: '',
    systemSenderEmail: '',
    isActive: true,
    invitationEmailSubject: '',
    invitationEmailBody: '',
    isNotificationEmailEnabled: false,
    notificationEmailSubject: '',
    notificationEmailBody: ''
  };

  constructor() {
    this.loadConfiguration();
  }

  setActiveTab(tab: 'configuration' | 'invitation' | 'notification'): void {
    this.activeTab.set(tab);
  }

  loadConfiguration(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.emailConfigService.getConfiguration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (config: TenantEmailConfiguration) => {
          this.hasExistingConfig.set(true);
          // Check if it's from local dev: either API says so, or no database fields (key, createdAtUtc)
          this.isLocalDevelopment.set(config.isLocalDevelopment || (!config.key && !config.createdAtUtc));
          this.secretKeyVaultName.set(config.graphClientSecretKeyVaultName || '');
          this.isSecretConfigured.set(config.isClientSecretConfigured);
          this.formData = {
            graphTenantId: config.graphTenantId,
            graphClientId: config.graphClientId,
            graphClientSecret: '',
            systemSenderEmail: config.systemSenderEmail,
            isActive: config.isActive,
            invitationEmailSubject: config.invitationEmailSubject || '',
            invitationEmailBody: config.invitationEmailBody || '',
            isNotificationEmailEnabled: config.isNotificationEmailEnabled,
            notificationEmailSubject: config.notificationEmailSubject || '',
            notificationEmailBody: config.notificationEmailBody || ''
          };
          this.isLoading.set(false);
          this.cdr.markForCheck(); // Notify CD after formData reassignment (zoneless-safe)
        },
        error: (error: Error) => {
          // 404 means no configuration exists yet - this is expected
          if (error.message.includes('not found')) {
            this.hasExistingConfig.set(false);
          } else {
            this.errorMessage.set(`Failed to load configuration: ${error.message}`);
          }
          this.isLoading.set(false);
        }
      });
  }

  onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    const request: SaveEmailConfigurationRequest = {
      graphTenantId: this.formData.graphTenantId.trim(),
      graphClientId: this.formData.graphClientId.trim(),
      graphClientSecret: this.formData.graphClientSecret?.trim() ? this.formData.graphClientSecret.trim() : null,
      systemSenderEmail: this.formData.systemSenderEmail.trim(),
      isActive: this.formData.isActive,
      invitationEmailSubject: this.formData.invitationEmailSubject?.trim(),
      invitationEmailBody: this.formData.invitationEmailBody?.trim(),
      isNotificationEmailEnabled: this.formData.isNotificationEmailEnabled,
      notificationEmailSubject: this.formData.notificationEmailSubject?.trim(),
      notificationEmailBody: this.formData.notificationEmailBody?.trim()
    };

    this.emailConfigService.saveConfiguration(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSaving.set(false);
          this.successMessage.set(response.message);
          this.hasExistingConfig.set(true);
          
          // Reload configuration to refresh secret status
          this.loadConfiguration();
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            this.successMessage.set('');
          }, 5000);
        },
        error: (error: Error) => {
          this.isSaving.set(false);
          this.errorMessage.set(`Failed to save configuration: ${error.message}`);
        }
      });
  }

  validateForm(): boolean {
    this.errorMessage.set('');

    if (!this.formData.graphTenantId.trim()) {
      this.errorMessage.set('Graph Tenant ID is required');
      return false;
    }

    if (!this.formData.graphClientId.trim()) {
      this.errorMessage.set('Graph Client ID is required');
      return false;
    }

    if (!this.formData.systemSenderEmail.trim()) {
      this.errorMessage.set('System Sender Email is required');
      return false;
    }

    if (!this.isSecretConfigured() && !this.formData.graphClientSecret.trim()) {
      this.errorMessage.set('Client secret is required');
      return false;
    }

    // Validate email format - using a safer regex pattern to prevent ReDoS
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(this.formData.systemSenderEmail.trim())) {
      this.errorMessage.set('Please enter a valid email address');
      return false;
    }

    return true;
  }

  sendTestInvitationEmail(): void {
    if (!this.formData.invitationEmailSubject?.trim()) {
      this.errorMessage.set('Subject is required to send a test email');
      return;
    }

    this.isSendingInvitationTest.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    this.emailConfigService
      .sendTestInvitationEmail({
        subject: this.formData.invitationEmailSubject,
        body: this.formData.invitationEmailBody,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSendingInvitationTest.set(false);
          this.successMessage.set(response.message);
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          this.isSendingInvitationTest.set(false);
          this.errorMessage.set(
            err?.message || 'Failed to send test email. Please try again.'
          );
          this.cdr.markForCheck();
        },
      });
  }

  sendTestNotificationEmail(): void {
    if (!this.formData.notificationEmailSubject?.trim()) {
      this.errorMessage.set('Subject is required to send a test email');
      return;
    }

    this.isSendingNotificationTest.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    this.emailConfigService
      .sendTestNotificationEmail({
        subject: this.formData.notificationEmailSubject,
        body: this.formData.notificationEmailBody,
        isNotificationEnabled: this.formData.isNotificationEmailEnabled,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSendingNotificationTest.set(false);
          this.successMessage.set(response.message);
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          this.isSendingNotificationTest.set(false);
          this.errorMessage.set(
            err?.message || 'Failed to send test email. Please try again.'
          );
          this.cdr.markForCheck();
        },
      });
  }

  onCancel(): void {
    if (this.hasExistingConfig()) {
      this.loadConfiguration();
    } else {
      this.resetForm();
    }
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  resetForm(): void {
    this.formData = {
      graphTenantId: '',
      graphClientId: '',
      graphClientSecret: '',
      systemSenderEmail: '',
      isActive: true,
      invitationEmailSubject: '',
      invitationEmailBody: '',
      isNotificationEmailEnabled: false,
      notificationEmailSubject: '',
      notificationEmailBody: ''
    };
    this.secretKeyVaultName.set('');
    this.isSecretConfigured.set(false);
    this.cdr.markForCheck(); // Notify CD after formData reassignment (zoneless-safe)
  }
}
